import {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from "react";
import { read, utils } from "xlsx";
import {
  getDemoRajEntity,
  getDemoRajTrader,
  getRajEntity,
  getRajTrader,
  PostBulkRajTransaction,
  PostRajTransaction,
} from "../../../../Rest_api/RestApi";
import Loading from "../../../../components/Loading";
import { invalidateCacheByPattern } from "../../../../Rest_api/Network_utility";

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  type?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  name: string;
  id?: string;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  required?: boolean;
  className?: string;
  showPlaceholder?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  name,
  id,
  options,
  placeholder = "Select...",
  required = false,
  className = "",
  defaultValue,
  showPlaceholder = true,
  ...rest
}) => {
  return (
    <>
      <label
        htmlFor={name}
        className="block text-sm font-medium text-gray-500 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <select
          defaultValue={defaultValue}
          id={name}
          name={name}
          className={`appearance-none border p-2 mt-1 h-[39px] text-sm rounded-lg text-[#9CA3AF] w-full pr-10 ${className}`}
          required={required}
          {...rest}
        >
          {showPlaceholder && (
            <option className="text-[#9CA3AF]" value="">
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* Custom arrow icon */}
        <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center px-2 mt-1 text-[#9CA3AF]">
          <svg
            className="fill-current h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
    </>
  );
};

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = "text",
  id,
  placeholder,
  required = false,
  className = "",
  ...rest
}) => {
  const isDateOrTimeInput = type === "date" || type === "time";

  const inputProps = {
    type,
    id: id || name,
    name,
    className: `border p-2 mt-1 rounded-lg text-[#9CA3AF] w-full h-[39px] ${
      isDateOrTimeInput ? "custom-date-time-input" : ""
    } ${className}`,
    placeholder: type === "time" ? "HH:MM" : placeholder,
    required,

    ...(type === "time"
      ? {
          step: "1",
        }
      : { step: "any" }),
    ...rest,
  };

  return (
    <>
      <label
        htmlFor={id || name}
        className="block text-sm font-medium text-gray-500 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        className={`relative ${
          isDateOrTimeInput ? "date-time-input-wrapper" : ""
        }`}
      >
        <input {...inputProps} />
      </div>
      <style>{`
  .custom-date-time-input::-webkit-calendar-picker-indicator {
    filter: invert(72%) sepia(9%) saturate(261%) hue-rotate(184deg) brightness(93%) contrast(85%);
    opacity: 1;
  }

  /* Ensure the input text doesn't overlap with our custom icon */
  .custom-date-time-input {
    padding-right: 10px;
  }
  
  /* Force 24-hour format in supported browsers */
  input[type="time"] {
    -moz-appearance: textfield;
    -webkit-appearance: textfield;
    appearance: textfield;
  }
`}</style>
    </>
  );
};

const Form = () => {
  const [entity, setEntity] = useState<string[]>([]);
  const [trader, setTrader] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);

  async function handleFetch() {
    setLoadingFull(true);
    const entity = await getDemoRajEntity();
    const trader = await getDemoRajTrader();

    setLoadingFull(false);
  }

  useEffect(() => {
    handleFetch();
  }, []);

  const handleSubmit = async (e: any) => {
    setLoading(true);
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    let data: { [key: string]: string | number } = {};

    for (const [key, value] of formData.entries()) {
      const inputElement = form.elements.namedItem(key) as HTMLInputElement;
      let processedValue = value;

      // Special handling for Contract Time To
      if (
        key === "gna_contract_time_to" &&
        typeof processedValue === "string"
      ) {
        processedValue = normalizeTime(processedValue);
      }

      if (inputElement && inputElement.type === "number") {
        // Number handling...
        data[key] =
          processedValue === "" ? "" : parseFloat(processedValue as string);
      } else {
        if (processedValue instanceof File) {
          data[key] = processedValue.name;
        } else {
          data[key] = processedValue as string;
        }
      }
    }

    try {
      const aa = await PostRajTransaction(data);
      if (aa.message == "Transaction created successfully") {
        alert("Transaction created successfully");
      }
      form.reset();
    } catch (error) {
      console.log(error);
      alert("Error in submitting the form");
    }
    setLoading(false);
  };

  function isValidDate(stringDate: string) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(stringDate);
  }

  function formatDateString(dateString: string): string {
    if (!dateString) return "";

    try {
      // Remove any spaces in the date string
      dateString = dateString.trim();

      // Check if the date already uses dashes
      if (dateString.includes("-")) {
        // Validate if it's already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
      }

      // Split by slashes or dots
      const parts = dateString.split(/[\/\.-]/);

      if (parts.length !== 3) return "";

      let year: string, month: string, day: string;

      // Determine format based on parts
      if (parts[0].length === 4) {
        // YYYY/MM/DD format
        [year, month, day] = parts;
      } else if (parseInt(parts[0]) > 12 && parseInt(parts[0]) <= 31) {
        // DD/MM/YYYY format
        [day, month, year] = parts;
      } else {
        // MM/DD/YYYY format
        [month, day, year] = parts;
      }

      // Handle 2-digit years
      if (year.length === 2) {
        year = `20${year}`;
      }

      // Pad with leading zeros if needed
      month = month.padStart(2, "0");
      day = day.padStart(2, "0");

      // Create the formatted date
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  }

  // Add this function to normalize time values
  function normalizeTime(timeValue: string): string {
    if (!timeValue) return "";

    // Check for 24:00 or 24:00:00 format
    if (timeValue === "24:00" || timeValue === "24:00:00") {
      return "23:59:59";
    }
    return timeValue;
  }

  function handleBulkUpload(e: any) {
    const file = e.target.files[0];
    e.target.value = null;

    if (file) {
      // Read file as binary string or array buffer
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const binary = e.target?.result;
          if (!binary) {
            throw new Error("Failed to read file");
          }

          /* parse with proper type handling */
          const wb = read(binary, {
            type: "binary",
            cellDates: true,
            dateNF: "yyyy-mm-dd",
          });

          /* get first worksheet */
          const ws = wb.Sheets[wb.SheetNames[0]];

          /* convert to JSON with headers */
          const rawData = utils.sheet_to_json(ws, {
            header: 1,
            raw: false,
            dateNF: "yyyy-mm-dd",
          });

          // Extract headers from first row
          const headers = rawData[0] as string[];

          // Create array of objects with proper headers
          const jsonData = [];
          for (let i = 1; i < rawData.length; i++) {
            const row = rawData[i] as any[];
            if (
              row.filter(
                (val) => val !== null && val !== undefined && val !== ""
              ).length === 0
            ) {
              // Skip completely empty rows
              continue;
            }

            const obj: Record<string, any> = {};
            for (let j = 0; j < headers.length; j++) {
              if (headers[j]) {
                obj[headers[j]] = row[j] === undefined ? "" : row[j];
              }
            }
            jsonData.push(obj);
          }

          // Map keys to our internal format
          const keyMap: Record<string, string> = {
            "Start Date": "start_date",
            "End Date": "end_date",
            "Withdrawal Loss (%)": "withdrawal_loss",
            "LOI Number": "loi_number",
            "LOI Date": "loi_date",
            "Schedule In": "schedule_in",
            "Type of Transaction": "type_of_transaction",
            "GNA Number": "gna_number",
            Trader: "trader",
            Entity: "entity",
            "Contract Date From": "gna_contract_date_from",
            "Contract Date To": "gna_contract_date_to",
            "Contract Time From": "gna_contract_time_from",
            "Contract Time To": "gna_contract_time_to",
            "Contract Capacity": "ppa_contract_capacity",
            Rate: "rate",
            "GNA Contract Created (mw)": "gna_contract_created",
            Request: "scheduling_request",
          };

          // Transform data with proper key mapping and formatting
          const transformedData = jsonData.map((row) => {
            const newRow: Record<string, any> = {};

            for (const [oldKey, value] of Object.entries(row)) {
              const newKey = keyMap[oldKey] || oldKey;
              let cleanedValue = value;

              // Clean string values by trimming whitespace
              if (typeof value === "string") {
                cleanedValue = value.trim();
              }

              if (newKey.includes("date") && cleanedValue) {
                try {
                  if (typeof cleanedValue === "string") {
                    // Format the date string to YYYY-MM-DD
                    const formattedDate = formatDateString(cleanedValue);

                    // Check if the formatted date is valid
                    if (formattedDate && isValidDate(formattedDate)) {
                      newRow[newKey] = formattedDate;
                    } else {
                      return alert(
                        `Invalid Date Format for ${oldKey}: ${cleanedValue}`
                      );
                    }
                  } else if (cleanedValue instanceof Date) {
                    // Handle Date objects (from Excel)
                    const year = cleanedValue.getFullYear();
                    const month = String(cleanedValue.getMonth() + 1).padStart(
                      2,
                      "0"
                    );
                    const day = String(cleanedValue.getDate()).padStart(2, "0");
                    newRow[newKey] = `${year}-${month}-${day}`;
                  } else {
                    return alert(`Invalid Date Format for ${oldKey}`);
                  }
                } catch (e) {
                  console.error(`Error formatting date for ${newKey}:`, e);
                  return alert(`Error formatting date for ${oldKey}`);
                }
                continue; // Skip the rest of the loop for this field
              }

              // Handle special case for RUVITL request
              if (
                newKey === "ruvitl_scheduling_request" &&
                (cleanedValue === "as per email" ||
                  (typeof cleanedValue === "string" &&
                    cleanedValue.toLowerCase() === "as per email"))
              ) {
                newRow["ruvitl_scheduling_request"] = 0;
                continue;
              }

              // Format times to HH:MM format
              if (newKey.includes("time") && typeof cleanedValue === "string") {
                // Special handling for "Contract Time To" with value 24:00
                if (
                  newKey === "gna_contract_time_to" ||
                  oldKey === "Contract Time To"
                ) {
                  cleanedValue = normalizeTime(cleanedValue);
                  // If it's already been normalized to 23:59:59, don't reformat it
                  if (cleanedValue === "23:59:59") {
                    newRow[newKey] = cleanedValue;
                    continue;
                  }
                }

                // Extract hours, minutes, and seconds from time string
                const timeMatch = cleanedValue.match(/(\d+):(\d+)(?::(\d+))?/);
                if (timeMatch) {
                  const hours = parseInt(timeMatch[1]);
                  const minutes = parseInt(timeMatch[2]);
                  const seconds = timeMatch[3] ? parseInt(timeMatch[3]) : 0;

                  cleanedValue = `${hours.toString().padStart(2, "0")}:${minutes
                    .toString()
                    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
                }
              }

              // Add special formatting for trader and entity fields - replace underscores with spaces
              if (newKey === "trader" || newKey === "entity") {
                if (typeof cleanedValue === "string") {
                  cleanedValue = cleanedValue.replace(/_/g, " ");
                }
              }

              // Special handling for null or empty values
              if (
                cleanedValue === null ||
                cleanedValue === undefined ||
                cleanedValue === ""
              ) {
                if (newKey === "loi_number") {
                  newRow[newKey] = 0;
                } else {
                  newRow[newKey] = "";
                }
              } else {
                // For non-null values, use the cleaned value
                // Convert specific fields to numbers
                const numberFields = [
                  "withdrawal_loss",
                  "ruvitl_scheduling_request",
                  "rate",
                  "ppa_contract_capacity",
                  "gna_number",
                  "gna_contract_created",
                ];

                if (numberFields.includes(newKey)) {
                  // Convert to number if the field should be numeric
                  const numValue = parseFloat(
                    String(cleanedValue).replace(/,/g, "")
                  );
                  if (!isNaN(numValue)) {
                    newRow[newKey] = numValue;
                  } else {
                    // If conversion fails, use empty string (except for loi_number)
                    newRow[newKey] = newKey === "loi_number" ? 0 : "";
                  }
                } else {
                  // For non-numeric fields, just use the cleaned value
                  newRow[newKey] = cleanedValue;
                }
              }
            }

            return newRow;
          });

          if (transformedData.length > 0) {
            handleBulkSubmit(
              transformedData.filter(
                (item) => item !== undefined && item !== null
              )
            );
          }
        } catch (error) {
          console.error("Error processing Excel file:", error);
          alert("Error processing Excel file. Please check the format.");
        }
      };
      reader.onerror = () => {
        console.error("Error reading file");
        alert("Error reading file");
      };

      reader.readAsBinaryString(file);
    }
  }

  async function handleBulkSubmit(data: Record<string, any>[]) {
    if (data.length === 0) {
      alert("No data to submit");
      return;
    }
    setLoadingFull(true);

    try {
      await PostBulkRajTransaction(data);
      invalidateCacheByPattern("/transactions/");
      alert("Bulk data submitted successfully");
    } catch (error) {
      console.error("Error submitting bulk data:", error);
      alert("Error in bulk submission");
    }

    setLoadingFull(false);
  }

  return (
    <>
      <>{loadingFull && <Loading />}</>

      <div className="flex items-center mt-4">
        <div className="flex gap-4 mb-4 justify-end w-full">
          <div>
            <label className="flex cursor-pointer items-center text-xs text-nowrap justify-between px-4 py-3 bg-[#F1935C] text-white rounded-xl transition">
              Upload CSV
              <input
                onChange={(e) => handleBulkUpload(e)}
                type="file"
                // accept=".csv"
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="">
        <div className="grid grid-cols-4 gap-6 px-4">
          <div className="">
            <InputField
              label="Start Date"
              name="start_date"
              placeholder="Enter Start Date"
              required
              type="date"
            />
          </div>
          <div className="">
            <InputField
              label="End Date"
              name="end_date"
              placeholder="Enter End Date"
              required
              type="date"
            />
          </div>
          <div className="">
            <InputField
              label="Withdrawal Loss (%)"
              name="withdrawal_loss"
              placeholder="Enter Withdrawal Loss"
              type="number"
            />
          </div>
          <div className="">
            <InputField
              label="LOI Number"
              name="loi_number"
              placeholder="Enter LOI Number"
              type="number"
            />
          </div>
          <div className="">
            <InputField
              label="LOI Date"
              name="loi_date"
              placeholder="Enter LOI Date"
              type="date"
            />
          </div>

          <div className="">
            <SelectField
              label="Schedule In"
              name="schedule_in"
              options={[
                { value: "GNA", label: "GNA" },
                { value: "TGNA", label: "TGNA" },
              ]}
              placeholder="Select Contract Type"
              required
              defaultValue={"GNA"}
              showPlaceholder={false}
            />
          </div>
          <div className="">
            <SelectField
              label="Type of Transaction"
              name="type_of_transaction"
              options={[
                { value: "LTA", label: "LTA" },
                { value: "UA", label: "UA" },
                { value: "DEEP TN-27", label: "DEEP TN-27" },
                { value: "LDC-RA", label: "LDC-RA" },
                { value: "INTRA STATE", label: "INTRA STATE" },
              ]}
              placeholder="Select Type of Transaction"
            />
          </div>
          <div className="">
            <InputField
              label="GNA Number"
              name="gna_number"
              placeholder="Enter GNA Number"
              type="number"
            />
          </div>
          <div className="">
            <SelectField
              label="Trader"
              name="trader"
              options={trader.map((e: any) => ({
                value: e.name,
                label: e.name,
              }))}
              placeholder="Select Trader"
              required
            />
          </div>
          <div className="">
            <SelectField
              label="Injecting Entity"
              name="entity"
              options={entity.map((e: any) => ({
                value: e.id,
                label: e.name,
              }))}
              placeholder="Select Injecting Entity"
              required
            />
          </div>
        </div>

        <div className="my-8 bg-[#FDFDFD] border border-[#f1f1f1] p-5 rounded-xl">
          <h2 className="text-[16px] text-[#8C8A96] font-medium ">
            GNA Contract Details :
          </h2>

          <div className="grid grid-cols-4 gap-10 mt-4">
            <div className="">
              <InputField
                label="Contract Date From"
                name="gna_contract_date_from"
                placeholder="Enter Contract Date From"
                type="date"
              />
            </div>
            <div className="">
              <InputField
                label="Contract Date To"
                name="gna_contract_date_to"
                placeholder="Enter Contract Date To"
                type="date"
              />
            </div>
            <div className="">
              <InputField
                label="Contract Time From"
                name="gna_contract_time_from"
                placeholder="Enter Contract Time From"
                type="time"
              />
            </div>
            <div className="">
              <InputField
                label="Contract Time To"
                name="gna_contract_time_to"
                placeholder="Enter Contract Time To"
                type="time"
              />
            </div>
          </div>
        </div>

        <div className="p-5 pt-0">
          <h2 className="text-[16px] text-[#8C8A96] font-medium ">
            PPA Contract Details :
          </h2>

          <div className="grid grid-cols-4 gap-10 mt-4">
            <div className="">
              <InputField
                label="Contract Capacity (mw)"
                name="ppa_contract_capacity"
                placeholder="Enter Contract Capacity"
                required
                type="number"
              />
            </div>
            <div className="">
              <InputField
                label="Rate"
                name="rate"
                placeholder="Enter Rate"
                type="number"
              />
            </div>
            <div className="">
              <InputField
                label="GNA Contract Created"
                name="gna_contract_created"
                placeholder="Enter GNA Contract Created"
                type="number"
              />
            </div>
            <div className="">
              <InputField
                label="Ruvitl Scheduling Request"
                name="ruvitl_scheduling_request"
                placeholder="Enter Ruvitl Scheduling Request"
                required
                type="number"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center mt-5">
          <button
            disabled={loading}
            type="submit"
            className="px-8 py-3 bg-primary text-white rounded-xl font-medium text-[0.8rem]"
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </>
  );
};

export default Form;
